/**
 * SOVRANOVA — audioBus
 *
 * ONE owner for everything that makes sound.
 *
 * THE PROBLEM THIS EXISTS FOR:
 * eleven components each created their own `new Audio()` and none of them knew
 * about each other. Move between features while one is speaking and the old
 * voice keeps going underneath the new one — David's report: "numerous voices
 * try to interact for each one and then start overlapping… quite annoying and
 * maybe a deterrent to users." The dashboard greeting clash (8a2aa20) was one
 * instance of this; this is the general case.
 *
 * WHY IT PATCHES window.Audio RATHER THAN ASKING NICELY:
 * a bus that components must remember to use is a bus that the next feature
 * forgets to use, and the bug comes straight back. Registering every audio
 * element at construction means correctness does not depend on anyone
 * remembering. It is one small, loud, well-commented global — deliberately, and
 * in preference to eleven chances to get it wrong.
 *
 * WHAT IT DOES NOT DO:
 * it never starts audio, never changes volume, and never touches the live Hume
 * voice connection (that is a WebRTC stream, not an <audio> element, and owns
 * its own lifecycle via connect/disconnect).
 */

const live = new Set()      // every Audio element currently known
let installed = false

// ── The one protected voice ─────────────────────────────────────────────────
// The daily welcome greeting (19 Aug). David's report: the first tap of the
// day both releases the held greeting AND often navigates somewhere — and the
// navigation's stopAllAudio() cut the welcome off mid-sentence. The welcome
// may keep speaking across a navigation; it still yields the INSTANT any other
// voice actually starts (see the 'play' listener in registerAudio), so voices
// can never stack. At most one element is ever protected.
let protectedEl = null

/** Mark one element as allowed to survive navigation. */
export function protectAudio(el) {
  if (!el) return el
  protectedEl = el
  const clear = () => { if (protectedEl === el) protectedEl = null }
  el.addEventListener('ended', clear, { once: true })
  el.addEventListener('error', clear, { once: true })
  el.addEventListener('pause', clear, { once: true })
  return el
}

/** Stop and forget one element. Never throws. */
function kill(el) {
  try {
    el.pause()
    el.currentTime = 0
    // Release the blob if we made one — these leak fast otherwise, and every
    // TTS reply creates one.
    if (el.src && el.src.startsWith('blob:')) URL.revokeObjectURL(el.src)
  } catch (_) {}
  live.delete(el)
}

/**
 * Silence everything. Call before starting new speech, and on any navigation.
 * `except` lets a caller keep the element it is about to play.
 * `opts.spareProtected` (navigation only) lets the daily welcome finish
 * speaking across a view change — every other call still silences it.
 */
export function stopAllAudio(except = null, opts = {}) {
  for (const el of Array.from(live)) {
    if (el === except) continue
    if (opts.spareProtected && el === protectedEl) continue
    kill(el)
  }
  // Browser speech synthesis is a separate pipeline with its own queue, and it
  // survives navigation happily. It has to be cancelled explicitly.
  try { window.speechSynthesis?.cancel() } catch (_) {}
}

/** Register an element so the bus can stop it later. Idempotent. */
export function registerAudio(el) {
  if (!el) return el
  live.add(el)
  // Self-deregister when it finishes naturally, so the set does not grow
  // without bound across a long session.
  el.addEventListener('ended', () => live.delete(el), { once: true })
  el.addEventListener('error', () => live.delete(el), { once: true })
  // ONE VOICE: the moment any other element actually starts speaking, the
  // protected welcome yields. This is what makes sparing it on navigation
  // safe — it can outlive a view change but never talk over another voice.
  el.addEventListener('play', () => {
    if (protectedEl && el !== protectedEl) { kill(protectedEl); protectedEl = null }
  })
  return el
}

/**
 * Install once, as early as possible. Every `new Audio()` from this point on is
 * registered automatically, including in components that know nothing about
 * this module.
 */
export function installAudioBus() {
  if (installed || typeof window === 'undefined' || !window.Audio) return
  installed = true

  const Native = window.Audio
  function TrackedAudio(...args) {
    const el = new Native(...args)
    registerAudio(el)
    return el
  }
  TrackedAudio.prototype = Native.prototype
  window.Audio = TrackedAudio

  // Leaving the tab should not leave a voice talking to an empty room.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopAllAudio()
  })
}
